import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/utils/api";
import {
  Eye, CheckCircle, AlertTriangle, RefreshCw, ArrowLeft, Package, Trash2,
} from "lucide-react";

const REWORKABLE = [
  { key: "plating_ng", label: "Plating NG" },
  { key: "burr_rework", label: "Burr (rework)" },
];

const NON_REWORKABLE = [
  { key: "bend", label: "Bend" },
  { key: "dent", label: "Dent" },
  { key: "side_cut", label: "Side Cut" },
  { key: "embossing_ng", label: "Embossing NG" },
  { key: "od_ng", label: "OD NG" },
  { key: "id_ng", label: "ID NG" },
  { key: "pin_ng", label: "Pin NG" },
  { key: "burr_scrap", label: "Burr (scrap)" },
  { key: "crack", label: "Crack" },
  { key: "scratch", label: "Scratch" },
  { key: "rust", label: "Rust" },
  { key: "others", label: "Others" },
];

const zeros = (keys) => keys.reduce((a, k) => ({ ...a, [k.key]: 0 }), {});

const VisualInspection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState(1); // 1: part, 2: batch, 3: counts, 4: done
  const [parts, setParts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [okQty, setOkQty] = useState(0);
  const [reworkable, setReworkable] = useState(zeros(REWORKABLE));
  const [nonReworkable, setNonReworkable] = useState(zeros(NON_REWORKABLE));
  const [remarks, setRemarks] = useState("");
  const [partSearch, setPartSearch] = useState("");
  const [batchSearch, setBatchSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const fetchParts = async () => {
    try {
      const res = await api.get("/visual-inspection/parts");
      setParts(res.data.parts || []);
    } catch (e) {
      setError("Could not load parts. Please try again.");
    }
  };

  const fetchBatches = async (part) => {
    try {
      const q = part.part_no
        ? `part_no=${encodeURIComponent(part.part_no)}`
        : `part_name=${encodeURIComponent(part.part_name)}`;
      const res = await api.get(`/visual-inspection/batches?${q}`);
      setBatches(res.data.batches || []);
    } catch (e) {
      setError("Could not load batches for this part.");
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get("/visual-inspection/history?limit=50");
      setHistory(res.data.records || []);
    } catch (err) {
      console.error("Failed to load visual inspection history", err);
    }
  };

  useEffect(() => { fetchParts(); fetchHistory(); }, []);

  const totals = useMemo(() => {
    const rework = Object.values(reworkable).reduce((a, b) => a + (Number(b) || 0), 0);
    const scrap = Object.values(nonReworkable).reduce((a, b) => a + (Number(b) || 0), 0);
    const total = (Number(okQty) || 0) + rework + scrap;
    return { rework, scrap, defects: rework + scrap, total };
  }, [okQty, reworkable, nonReworkable]);

  const stage2Ok = selectedBatch?.qty ?? 0; // batch qty at pick time (approx. stage2_ok)
  const discrepancy = stage2Ok && Number(okQty) < stage2Ok
    ? stage2Ok - Number(okQty)
    : 0;

  const filteredParts = parts.filter((p) => {
    if (!partSearch.trim()) return true;
    const q = partSearch.toLowerCase();
    return (p.part_name || "").toLowerCase().includes(q) || (p.part_no || "").toLowerCase().includes(q);
  });

  const filteredBatches = batches.filter((b) => {
    if (!batchSearch.trim()) return true;
    const q = batchSearch.toLowerCase();
    return (b.fg_batch_no || "").toLowerCase().includes(q)
      || (b.rm_batch_code || "").toLowerCase().includes(q);
  });

  const pickPart = async (p) => {
    setSelectedPart(p);
    setBatchSearch("");
    setBatches([]);
    await fetchBatches(p);
    setStep(2);
  };

  const pickBatch = (b) => {
    setSelectedBatch(b);
    setOkQty(b.qty || 0);
    setReworkable(zeros(REWORKABLE));
    setNonReworkable(zeros(NON_REWORKABLE));
    setRemarks("");
    setStep(3);
  };

  const resetAll = () => {
    setStep(1);
    setSelectedPart(null);
    setSelectedBatch(null);
    setOkQty(0);
    setReworkable(zeros(REWORKABLE));
    setNonReworkable(zeros(NON_REWORKABLE));
    setRemarks("");
    setResult(null);
    setError("");
  };

  const submit = async () => {
    if (submitting) return;
    if (!selectedBatch) return;
    if (totals.total === 0) {
      setError("Enter at least one OK or defect quantity.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await api.post("/visual-inspection/submit", {
        fg_batch_no: selectedBatch.fg_batch_no,
        ok_qty: Number(okQty) || 0,
        reworkable,
        non_reworkable: nonReworkable,
        remarks,
      });
      setResult(res.data.record);
      setStep(4);
      fetchHistory();
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Try again.");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <Header />

      {/* Sub-header */}
      <div className="bg-[#1A3560] text-white px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              data-testid="vi-back-btn"
              onClick={() => step > 1 && step < 4 ? setStep(step - 1) : navigate("/home")}
              className="bg-white/10 hover:bg-white/20 rounded-lg p-2"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-lg font-bold">Visual Inspection</h1>
              <p className="text-[11px] text-white/60">दृश्य निरीक्षण · Post-production audit</p>
            </div>
          </div>
          <button
            data-testid="vi-history-btn"
            onClick={() => setShowHistory(!showHistory)}
            className="bg-white/10 hover:bg-white/20 text-xs font-bold px-3 py-2 rounded-lg"
          >
            {showHistory ? "Hide" : "History"} ({history.length})
          </button>
        </div>
      </div>

      {/* Stepper */}
      {step < 4 && (
        <div className="bg-white border-b border-slate-200 px-4 py-3">
          <div className="max-w-2xl mx-auto flex items-center gap-2">
            {["Part", "Batch", "Count"].map((label, i) => {
              const active = step === i + 1;
              const done = step > i + 1;
              return (
                <div key={label} className="flex items-center gap-2 flex-1">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${
                    active ? "bg-[#1A3560] text-white" : done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"
                  }`}>
                    {done ? "✓" : i + 1}
                  </div>
                  <p className={`text-xs font-bold ${active ? "text-[#1A3560]" : done ? "text-emerald-700" : "text-slate-400"}`}>
                    {label}
                  </p>
                  {i < 2 && <div className="flex-1 h-px bg-slate-200 mx-1"></div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {error && (
        <div data-testid="vi-error" className="max-w-2xl mx-auto mt-3 px-4">
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-sm flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {/* STEP 1 — pick part */}
        {step === 1 && (
          <div className="space-y-3">
            <input
              data-testid="vi-part-search"
              type="text"
              value={partSearch}
              onChange={(e) => setPartSearch(e.target.value)}
              placeholder="Search part name / part number..."
              className="w-full h-11 px-3 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A3560]"
            />
            <p className="text-xs uppercase font-bold tracking-widest text-slate-400">
              Pick a part ({filteredParts.length} available)
            </p>
            <div className="space-y-2">
              {filteredParts.length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm">
                  <Package size={28} className="mx-auto mb-2 opacity-30" />
                  No parts with FG batches yet.
                </div>
              ) : filteredParts.map((p) => (
                <button
                  key={`${p.part_no}|${p.part_name}`}
                  data-testid={`vi-part-${p.part_no || p.part_name}`}
                  onClick={() => pickPart(p)}
                  className="w-full text-left p-3 bg-white rounded-xl border border-slate-200 hover:border-[#1A3560] hover:shadow-sm active:scale-[0.98] transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900 truncate">{p.part_name || "(unnamed)"}</p>
                      <p className="text-xs font-mono text-slate-400 truncate">{p.part_no || "—"}</p>
                    </div>
                    <span className="text-[11px] font-bold bg-slate-100 text-slate-700 px-2 py-1 rounded-full">
                      {p.batch_count} batch{p.batch_count === 1 ? "" : "es"}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 2 — pick FG batch */}
        {step === 2 && (
          <div className="space-y-3">
            <div className="bg-white rounded-xl border border-slate-200 p-3">
              <p className="text-[11px] uppercase font-bold tracking-widest text-slate-400">Selected part</p>
              <p className="font-bold text-slate-900">{selectedPart?.part_name}</p>
              <p className="text-xs font-mono text-slate-500">{selectedPart?.part_no}</p>
            </div>
            <input
              data-testid="vi-batch-search"
              type="text"
              value={batchSearch}
              onChange={(e) => setBatchSearch(e.target.value)}
              placeholder="Search FG batch / RM batch..."
              className="w-full h-11 px-3 rounded-xl border border-slate-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#1A3560]"
            />
            <p className="text-xs uppercase font-bold tracking-widest text-slate-400">
              Pick FG batch ({filteredBatches.length})
            </p>
            <div className="space-y-2">
              {filteredBatches.length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm">No FG batches for this part.</div>
              ) : filteredBatches.map((b) => (
                <button
                  key={b.fg_batch_no}
                  data-testid={`vi-batch-${b.fg_batch_no}`}
                  onClick={() => pickBatch(b)}
                  className="w-full text-left p-3 bg-white rounded-xl border border-slate-200 hover:border-[#1A3560] hover:shadow-sm active:scale-[0.98] transition-all"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-mono font-bold text-[#1A3560] break-all">{b.fg_batch_no}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">RM: {b.rm_batch_code || "—"} · Job: {b.job_id || "—"}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-lg font-bold text-emerald-700">{b.qty}</p>
                      <p className="text-[10px] text-slate-400">pcs {b.visually_cleared ? "· ✓ visual" : ""}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 3 — enter counts */}
        {step === 3 && selectedBatch && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0">
                  <p className="font-bold text-slate-900 truncate">{selectedBatch.part_name}</p>
                  <p className="text-xs font-mono text-slate-400 break-all">{selectedBatch.fg_batch_no}</p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-slate-400">Available</p>
                  <p className="text-2xl font-extrabold text-emerald-700">{selectedBatch.qty}</p>
                </div>
              </div>
              {selectedBatch.visually_cleared && (
                <div className="mt-2 text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-2.5 py-1.5">
                  This batch was already visually inspected once (scrap: {selectedBatch.visual_ng_scrap_qty}, rework: {selectedBatch.visual_rework_qty || 0}). New submissions will decrement further.
                </div>
              )}
            </div>

            {/* OK count */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <label className="text-xs uppercase font-bold tracking-widest text-emerald-700 flex items-center gap-1.5">
                <CheckCircle size={12} /> OK Quantity
              </label>
              <input
                data-testid="vi-ok-qty"
                type="number"
                min="0"
                value={okQty}
                onChange={(e) => setOkQty(e.target.value.replace(/[^0-9]/g, ""))}
                className="mt-2 w-full h-14 text-2xl font-extrabold text-center text-emerald-700 bg-emerald-50 border-2 border-emerald-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
              {discrepancy > 0 && (
                <p className="text-[11px] text-amber-700 mt-2">
                  ⚠️ Discrepancy: visual OK ({okQty}) is {discrepancy} less than the FG batch quantity ({stage2Ok}). Difference will be recorded for QA review.
                </p>
              )}
            </div>

            {/* Reworkable */}
            <DefectSection
              title="Reworkable Defects (Rework)"
              accent="amber"
              defects={REWORKABLE}
              values={reworkable}
              onChange={setReworkable}
              total={totals.rework}
              testidPrefix="vi-rw"
            />

            {/* Non-Reworkable */}
            <DefectSection
              title="Non-Reworkable Defects (Scrap)"
              accent="red"
              defects={NON_REWORKABLE}
              values={nonReworkable}
              onChange={setNonReworkable}
              total={totals.scrap}
              testidPrefix="vi-nrw"
            />

            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <label className="text-xs uppercase font-bold tracking-widest text-slate-400">Remarks (optional)</label>
              <textarea
                data-testid="vi-remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={2}
                placeholder="Any observations..."
                className="mt-2 w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1A3560] resize-none"
              />
            </div>

            {/* Sticky totals + submit */}
            <div className="sticky bottom-2 bg-white rounded-2xl border-2 border-slate-300 shadow-lg p-4">
              <div className="grid grid-cols-4 gap-2 text-center mb-3">
                <div>
                  <p className="text-[10px] uppercase font-bold text-slate-400 tracking-widest">Total</p>
                  <p className="font-extrabold text-slate-900">{totals.total}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-emerald-700 tracking-widest">OK</p>
                  <p className="font-extrabold text-emerald-700">{Number(okQty) || 0}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-amber-700 tracking-widest">Rework</p>
                  <p className="font-extrabold text-amber-700">{totals.rework}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-red-700 tracking-widest">Scrap</p>
                  <p className="font-extrabold text-red-700">{totals.scrap}</p>
                </div>
              </div>
              <button
                data-testid="vi-submit"
                onClick={submit}
                disabled={submitting || totals.total === 0}
                className="w-full h-12 bg-[#15803D] text-white text-sm font-bold rounded-xl hover:bg-[#0F5D2D] active:scale-[0.98] disabled:opacity-50 transition-all"
              >
                {submitting ? "Submitting..." : "Submit Visual Inspection"}
              </button>
            </div>
          </div>
        )}

        {/* STEP 4 — success */}
        {step === 4 && result && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border-2 border-emerald-300 p-6 text-center">
              <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                <CheckCircle size={32} className="text-emerald-700" />
              </div>
              <p className="text-lg font-extrabold text-slate-900">Recorded</p>
              <p className="text-[11px] font-mono text-slate-400 mt-1">{result.visual_id}</p>
              <p className="text-xs text-slate-500 mt-2">
                {result.non_reworkable_total > 0
                  ? `FG batch qty reduced by ${result.non_reworkable_total} scrap pcs.`
                  : "No FG quantity change (no scrap defects)."}
              </p>
              {result.discrepancy_from_stage2 > 0 && (
                <div className="mt-3 text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-2.5 py-2">
                  ⚠️ Discrepancy of {result.discrepancy_from_stage2} pc(s) below Stage-2 OK — flagged for QA.
                </div>
              )}
            </div>
            <button
              data-testid="vi-new-btn"
              onClick={resetAll}
              className="w-full h-12 bg-[#1A3560] text-white text-sm font-bold rounded-xl hover:bg-[#102340] active:scale-[0.98]"
            >
              + New Visual Inspection
            </button>
          </div>
        )}

        {/* History drawer */}
        {showHistory && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mt-4">
            <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Recent (50)</p>
              <button onClick={fetchHistory} className="text-slate-400 hover:text-slate-700"><RefreshCw size={12} /></button>
            </div>
            {history.length === 0 ? (
              <div className="text-center py-6 text-slate-400 text-sm">No visual inspections yet.</div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                {history.map((h) => (
                  <div key={h.visual_id} className="border-b border-slate-100 px-3 py-2.5 text-xs">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-mono font-bold text-[#1A3560]">{h.visual_id}</p>
                        <p className="text-slate-600 truncate">{h.part_name}</p>
                        <p className="text-[10px] font-mono text-slate-400 break-all">{h.fg_batch_no}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">{h.inspector_name} · {(h.submitted_at || "").split("T")[0]}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-bold text-emerald-700">{h.ok_qty} OK</p>
                        <p className="text-[10px] text-amber-700">{h.reworkable_total} rw</p>
                        <p className="text-[10px] text-red-700">{h.non_reworkable_total} sc</p>
                        {h.discrepancy_from_stage2 > 0 && (
                          <p className="text-[10px] font-bold text-amber-700">Δ {h.discrepancy_from_stage2}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const DefectSection = ({ title, accent, defects, values, onChange, total, testidPrefix }) => {
  const colours = accent === "red"
    ? { border: "border-red-200", text: "text-red-700", bg: "bg-red-50", input: "border-red-200 bg-red-50 focus:ring-red-400 text-red-900" }
    : { border: "border-amber-200", text: "text-amber-700", bg: "bg-amber-50", input: "border-amber-200 bg-amber-50 focus:ring-amber-400 text-amber-900" };

  const clearAll = () => onChange(zeros(defects));

  return (
    <div className={`bg-white rounded-xl border ${colours.border} p-4`}>
      <div className="flex items-center justify-between mb-3">
        <p className={`text-xs uppercase font-bold tracking-widest ${colours.text}`}>{title}</p>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold ${colours.text}`}>{total}</span>
          {total > 0 && (
            <button onClick={clearAll} className="text-[10px] font-bold text-slate-400 hover:text-slate-700 flex items-center gap-0.5">
              <Trash2 size={10} /> clear
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {defects.map((d) => (
          <div key={d.key}>
            <label className="text-[11px] font-medium text-slate-600 block mb-1">{d.label}</label>
            <input
              data-testid={`${testidPrefix}-${d.key}`}
              type="number"
              min="0"
              value={values[d.key] || 0}
              onChange={(e) => onChange({ ...values, [d.key]: e.target.value.replace(/[^0-9]/g, "") })}
              className={`w-full h-10 text-center font-bold text-lg border-2 rounded-lg focus:outline-none focus:ring-2 ${colours.input}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default VisualInspection;
